// The front door: start a run, play somebody, or open a scenario.
//
// Until single player existed the mode was implicit in the URL — `?room=` meant
// networked and its absence meant hot-seat — which worked for testing and not
// at all for a player, who had no way to start a networked game without being
// handed a link by someone who already had one.
//
// **Three doors, and only one unfolds at a time.** The menu grew a card every
// time single player did, and by the end the first thing a new player saw was
// four ways to start that all looked equally important.
//
// On the kit since U9 (PLAN-UI §28.0, priority 2). The doors are in `menu/`;
// what is left here is the state and the order the page is in.

import { useEffect, useState } from "react";

import { sound } from "src/client/ai/audio/engine";

import { loadName, saveName } from "./playerName";
import { markNoticeSeen, noticeSeen } from "./analyticsNotice";
import { usePackPref } from "./packPref";
import type { Catalog, LevelName as Level } from "src/api";
import { type BoardMapId } from "src/engine/rules/maps";
import { savedRun } from "src/engine/game/runSave";
import { Footer } from "./Footer";
import { Btn, Icon, Link, Notice, Page, PageColumn, Text, styled } from "./kit";
import { ChipChoice } from "./menu/ChipChoice";
import { DiscardDialog } from "./menu/DiscardDialog";
import { DoorCard, DoorBody } from "./menu/DoorCard";
import { SettingsPanel, type TelemetryGate } from "./menu/SettingsPanel";
import { VersusDoor } from "./menu/VersusDoor";
import { difficultyText, playedText, sinceText } from "./menu/save";
import { useCatalog } from "./menu/useCatalog";

export { playedText };

/**
 * `watch-run` is `run` with nobody in the seat, exactly as `watch` is `solo`
 * with nobody in it: the machine plays the whole single-player loop and the
 * save is left alone, because nothing about it is the player's.
 */
export type LaunchMode =
  | "local"
  | "net"
  | "solo"
  | "run"
  | "training"
  | "continue"
  | "watch"
  | "watch-run";

export interface LaunchOptions {
  room?: string;
  /**
   * Which board to open on (`rules/maps.ts`), for the modes that open one.
   *
   * Set by whoever *starts* the game and by nobody else: a client that joined
   * a room replays the board out of its log, so this is undefined there and
   * the opening command is never sent twice.
   */
  map?: BoardMapId;
  scenario?: string;
  /**
   * Which team each army is on, indexed by side, for the boards with enough
   * armies to divide. From the scenario screen only.
   *
   * The assignment rather than the name of a layout: the screen offers the
   * named ones as quick fills and lets any single army be moved afterwards.
   */
  teams?: readonly number[];
  /** Difficulty, not a map. The authored level is chosen by the run itself. */
  level?: Level;
  /**
   * The opening roll, or undefined for a fresh one.
   *
   * A seed decides every drop, every wave and every blunder, so two games on
   * one seed are the same game — which is what makes a bug somebody saw once
   * a bug anybody can look at.
   */
  seed?: number;
}

interface Props {
  onLaunch(mode: LaunchMode, options?: LaunchOptions): void;
  /** Open the scenario screen, which is where a scenario is set up now. */
  onScenarios(): void;
  /** Set when we arrived here after a failed join, so we can say what happened. */
  error?: string;
  /** Open the rules. */
  onRules?(): void;
  telemetry?: TelemetryGate;
  /** Opens the privacy notice. Every door on this page needs one. */
  onPrivacy?: () => void;
  onTerms?: () => void;
  /** The game's tables, where the menu is drawn outside the `GameContext`. */
  catalog?: Catalog;
}

/** Accepts a full share link or a bare room id, and returns the id. */
export function roomIdFrom(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // A pasted link is the common case — nobody types a twenty-character id.
  const fromUrl = trimmed.match(/[?&]room=([a-z0-9]+)/i);
  const candidate = fromUrl ? fromUrl[1] : trimmed;
  return /^[a-z0-9]{6,32}$/.test(candidate) ? candidate : null;
}

const Column = styled(PageColumn)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.lg,
  // Deeper than the top on purpose. Scrolled to the end, a last card flush
  // against the window edge reads as a page that has been cut off rather than
  // one that has finished.
  paddingBottom: 64
}));

/**
 * **The gear is taken out of the flow so the wordmark can be centred on the
 * column rather than on what is left of it.** In a row the two share the width,
 * so a logo with `margin: auto` sits half the gear's width left of centre —
 * which is a wordmark that is *nearly* centred, and reads as a mistake in a way
 * that being plainly off-centre does not.
 */
const Brand = styled("div")({ position: "relative" });

/**
 * The wordmark, not a heading set in the interface font. It is the one thing on
 * this page that is not a control or a sentence, and the title is drawn *into*
 * the art — so it carries its own `alt` and nothing repeats it in text beside
 * it.
 *
 * `width`, not a bare `maxWidth`: an image with no width is laid out at its
 * intrinsic 960px until it decodes, which on the front door is the whole column
 * jumping the moment it arrives. The `aspectRatio` reserves the height for the
 * same reason, and both numbers are the file's own — see `tools/buildBrand.mjs`.
 */
const Logo = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: 360,
  aspectRatio: "960 / 538",
  display: "block",
  margin: "0 auto",
  // The plaque behind FACTORY is a dark grey on a near-black page, so the shape
  // needs a little separation from the ground it stands on. A drop shadow
  // rather than a border: the art is not a rectangle.
  filter: `drop-shadow(0 2px 10px ${theme.kit.color.shadow})`
}));

const Gear = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  display: "grid",
  placeItems: "center",
  width: 32,
  height: 32,
  padding: 0,
  border: 0,
  background: "transparent",
  color: on ? theme.kit.color.brand : theme.kit.color.inkDim,
  cursor: "pointer",
  "&:hover": { color: theme.kit.color.ink },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  }
}));

/** The footer's two document links. Words, not buttons wearing a plate. */
const DocLink = styled(Link.withComponent("button"))({
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  fontSize: 12,
  textDecoration: "underline",
  cursor: "pointer"
});

export function ModeMenu({
  onLaunch,
  error,
  onRules,
  onScenarios,
  telemetry,
  onPrivacy,
  onTerms,
  catalog: given
}: Props) {
  const catalog = useCatalog(given);
  /** Which of the three doors is open, if any. */
  const [open, setOpen] = useState<"versus" | null>(null);
  /** Settings, behind the gear. Not a door — see `SettingsPanel`. */
  const [settings, setSettings] = useState(false);
  /** Which piece set the board draws. Remembered — see `packPref.ts`. */
  const [pack, setPack] = usePackPref();
  /**
   * How hard a run should get. A *ceiling* rather than a starting point — see
   * `levelForStage`: every run opens easy, because the first camp is the one
   * where you have a king, a few heaps and no idea what a refinery is for.
   */
  const [level, setLevel] = useState<Level>("normal");
  // Read once, when the menu mounts. It cannot change while the menu is on
  // screen — nothing here plays a move — and re-reading storage on every render
  // would be a synchronous disk hit per frame.
  const [save] = useState(savedRun);
  /**
   * Which board a game opened from here is played on (`rules/maps.ts`).
   *
   * Classic by default, because that is chess and this door is the one a player
   * who has never seen a belt comes through. It reaches both ways of *opening*
   * a game — a room and a hot seat — and neither of them through the URL: a
   * link is an invitation to a room whose board is already decided, and the map
   * arrives in that room's log like everything else about it.
   */
  const [map, setMap] = useState<BoardMapId>("classic");
  const [joining, setJoining] = useState(false);
  // Read once for the same reason `save` is: it cannot change while the menu is
  // on screen except by the button that also sets the state.
  const [notice, setNotice] = useState(() => !noticeSeen());
  const [name, setName] = useState(loadName);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(error ?? null);
  /**
   * Which run the "this throws your save away" question is about, or null
   * when it is not on screen: Normal Mode (`run`) or Training (§31 G1, G3).
   */
  const [discarding, setDiscarding] = useState<"run" | "training" | null>(null);

  /**
   * The menu's own beds.
   *
   * Empty deps and no cleanup, and both are the design rather than a shortcut:
   * the request is deduped against what is *playing*, so a remount is free and
   * cannot restart the track, and a bed is left running until the other screen
   * asks for its own — the crossfade is what a mood change sounds like.
   */
  useEffect(() => {
    sound.music.request("menu");
    sound.ambience.request("menu");
  }, []);

  async function createGame() {
    setBusy(true);
    setProblem(null);
    try {
      // Room ids come from the server, never invented here: they gate access to
      // a game, so they have to be unguessable.
      const response = await fetch("/api/room");
      if (!response.ok) throw new Error(`server said ${response.status}`);
      const { room } = (await response.json()) as { room: string };
      onLaunch("net", { room, map });
    } catch (cause) {
      setProblem(
        `Could not reach the game server (${
          cause instanceof Error ? cause.message : "unknown error"
        }). Local play still works.`
      );
      setBusy(false);
    }
  }

  function joinGame() {
    const room = roomIdFrom(input);
    if (!room) {
      setProblem("That does not look like a game link. Paste the whole link, or its id.");
      return;
    }
    onLaunch("net", { room });
  }

  return (
    <Page>
      <Column width={480}>
        <div>
          <Brand>
            <Logo src="/brand/logo.png" alt="Factory Chess" data-testid="menu-logo" />
            {/* A gear, and it says what it is to a screen reader — an icon with
                no name is a button nobody can find twice. `aria-expanded`
                because it unfolds a panel in place rather than going
                somewhere. */}
            <Gear
              type="button"
              data-testid="menu-settings"
              on={settings}
              aria-label="Settings"
              aria-expanded={settings}
              onClick={() => setSettings((was) => !was)}
            >
              <Icon name="settings" size={18} />
            </Gear>
          </Brand>
          <Text tone="dim" size={13} block style={{ marginTop: 6, lineHeight: 1.5 }}>
            Chess, on a board that is also a factory. Captures leave scrap; pawns
            mine it, belts haul it, rooks smelt it into new pieces.
          </Text>
          {/*
            **What the password used to say, said out loud instead.** The gate
            was never really about keeping people out — it was about nobody
            arriving expecting a finished game.

            A line rather than a dismissible banner: it is true for as long as it
            is true, and a notice somebody can close is a notice half the
            visitors never read.
          */}
          <Notice tone="warn" data-testid="menu-prototype" style={{ marginTop: 10 }}>
            <strong>This is a prototype.</strong> It is under active development
            and changes several times a day — expect rough edges, and expect
            saved runs and rooms to stop working after an update. Do tell us what
            you find.
          </Notice>
          {onRules && (
            <Btn
              small
              variant="ghost"
              data-testid="menu-rules"
              style={{ marginTop: 8 }}
              onClick={onRules}
            >
              How to play
            </Btn>
          )}
        </div>

        {settings && (
          <SettingsPanel pack={pack} onPack={setPack} telemetry={telemetry} />
        )}

        {/*
          Continue is the primary door when there is a run to come back to. A
          player who left one mid-way did not come here to start another, and
          burying that behind "Start a run" makes the obvious button the
          destructive one.
        */}
        {save && (
          <DoorCard
            title="Continue"
            // Three facts, in the order they answer "should I press this":
            // which run it is, how far in, and when. Time played sits with the
            // plies because it is the other half of the same question — a
            // hundred plies over twenty minutes and a hundred over three hours
            // are different runs to come back to.
            detail={
              `${save.level}${difficultyText(catalog.difficulties(), save.difficulty)} — ` +
              `${save.plies} ${save.plies === 1 ? "ply" : "plies"} in` +
              `${save.played ? `, ${playedText(save.played)} played` : ""}` +
              `, ${sinceText(save.saved)}.`
            }
            action={
              <Btn
                variant="primary"
                data-testid="mode-continue"
                onClick={() => onLaunch("continue")}
              >
                Continue
              </Btn>
            }
          />
        )}

        <DoorCard
          title={save ? "Start over" : "Start a run"}
          detail="Normal Mode: a generated board, an enemy faction across it, and more arriving every few rounds. Training: a short drawn map, a stockpile to build with, and undo that reaches back past End Turn."
          action={
            <div style={{ display: "flex", gap: 8 }}>
              {/* Training first in reading order, Normal the primary: the
                  door a returning player wants is the real run. */}
              <Btn
                data-testid="mode-training"
                onClick={() => {
                  if (save) setDiscarding("training");
                  else onLaunch("training", { level });
                }}
              >
                Training
              </Btn>
              <Btn
                variant={save ? "default" : "primary"}
                data-testid="mode-run"
                onClick={() => {
                  // Say it out loud. The save is the only copy, and a misclick on
                  // the card *next to* Continue should not be how it goes.
                  if (save) setDiscarding("run");
                  else onLaunch("run", { level });
                }}
              >
                {save ? "Start over" : "Begin"}
              </Btn>
            </div>
          }
        >
          {/* How hard it *gets*, not how hard it opens. Every run starts easy —
              see `levelForStage` — because the first camp is the one where you
              have a king, a few heaps and no idea what a refinery is for. */}
          <DoorBody>
            <ChipChoice
              label="Works up to"
              value={level}
              onChange={(next) => setLevel(next as Level)}
              // How hard a run *gets*, as the chip row wants it.
              options={catalog.difficulties()}
              testPrefix="run-level"
            />
          </DoorBody>
        </DoorCard>

        <DoorCard
          title="Versus"
          detail="Two players. Create a game and share the link or the code, enter one you were sent, or hand one screen back and forth."
          action={
            <Btn
              data-testid="mode-versus"
              onClick={() => setOpen(open === "versus" ? null : "versus")}
            >
              {open === "versus" ? "Close" : "Choose"}
            </Btn>
          }
        >
          {open === "versus" && (
            <VersusDoor
              maps={catalog.maps()}
              map={map}
              onMap={setMap}
              joining={joining}
              onJoining={setJoining}
              input={input}
              onInput={setInput}
              onJoin={joinGame}
              onCreate={createGame}
              busy={busy}
              name={name}
              onName={(value) => {
                setName(value);
                // Persisted as it is typed, so it survives the reload that
                // joining a game from a link involves.
                saveName(value);
              }}
              onHotSeat={() => onLaunch("local", { map })}
            />
          )}
        </DoorCard>

        {/*
          The scenario door leads to a screen of its own (`ScenarioScreen`). It
          was an expansion of this card, which was fine while it was two chips
          and a list and stopped being fine the moment a scenario had settings —
          sliders, a seed and a choice of who plays, wedged in under a heading
          with the other two doors still sitting underneath.
        */}
        <DoorCard
          title="Scenario"
          detail="A board set up to show one thing: a working factory, a graveyard to mine, a lane that runs the wrong way. Play it, or watch two machines play it."
          action={
            <Btn data-testid="mode-scenario" onClick={() => onScenarios()}>
              Choose
            </Btn>
          }
        />

        {problem && (
          <Notice tone="bad" data-testid="menu-error">
            {problem}
          </Notice>
        )}

        {/*
          What is collected, said on the screen the game opens on.

          **At the bottom and in the flow, not fixed over the board.** It is a
          disclosure rather than an interruption: it belongs where somebody
          reading the menu will meet it, and it must not be the thing standing
          between them and the game.

          **Closing it is closing it, and the copy does not pretend otherwise.**
          There is no "allow" here because page analytics are not a choice this
          screen is offering — the choice that exists is whether *games* are
          sent, which has its own dialog and its own setting.
        */}
        {notice && (
          <Notice data-testid="analytics-notice">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <Text tone="dim" size={12} block style={{ flex: 1, lineHeight: 1.6 }}>
                This site collects anonymous play statistics — page views, how far
                a run gets, and totals like moves made and ore dug when one ends.
                They are counts, not a recording. A copy of a game itself is only
                sent if you say yes when asked, and you can change that under the
                gear.
              </Text>
              <Btn
                small
                variant="ghost"
                data-testid="analytics-notice-close"
                aria-label="Close the analytics notice"
                onClick={() => {
                  markNoticeSeen();
                  setNotice(false);
                }}
              >
                Close
              </Btn>
            </div>
          </Notice>
        )}

        {/* Last in the column, which is what "footer" means on a page that is a
            single centred stack rather than a layout with regions. */}
        <Footer>
          {onPrivacy && (
            <DocLink type="button" data-testid="menu-privacy" onClick={onPrivacy}>
              What we keep
            </DocLink>
          )}
          {/* Beside the privacy notice, because two documents about the same
              agreement should not be in two different places. */}
          {onTerms && (
            <DocLink type="button" data-testid="menu-terms" onClick={onTerms}>
              Terms of use
            </DocLink>
          )}
        </Footer>
      </Column>

      <DiscardDialog
        open={discarding !== null}
        onClose={() => setDiscarding(null)}
        onConfirm={() => {
          const kind = discarding ?? "run";
          setDiscarding(null);
          onLaunch(kind, { level });
        }}
        save={save}
      />
    </Page>
  );
}
